// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  BankPositionAssetType,
  type BankPosition,
} from '@client/banks/types/position';
import type {
  AssetTypeClassifier,
  AssetsSearchResp,
} from '@shared/data-access/interfaces/api/ai/assets';
import { assetTypeMap } from '@shared/data-access/transformers/assets/group-positions-by-asset-type';
import dayjs from 'dayjs';

export const mapSearchAssetType = (assetType: string): BankPositionAssetType => {
  if (assetType.includes('etf')) {
    return BankPositionAssetType.Etf;
  }

  if (assetType.includes('mutual_fund') || assetType.includes('pension')) {
    return BankPositionAssetType.MutualFund;
  }

  if (assetType.includes('stock')) {
    return BankPositionAssetType.Stock;
  }

  return BankPositionAssetType.Other;
};

export const mapAssetsSearchToInfoAssetType = (
  assetType: string,
): 'stock' | 'etf' | 'mutual_fund' | null => {
  const fromClassifier = assetTypeMap[assetType as AssetTypeClassifier];

  if (
    fromClassifier === 'stock' ||
    fromClassifier === 'etf' ||
    fromClassifier === 'mutual_fund'
  ) {
    return fromClassifier;
  }

  const fromSearch = mapSearchAssetType(assetType);

  if (fromSearch === BankPositionAssetType.Other) {
    return null;
  }

  return fromSearch;
};

export const mapAssetsSearchToPositionFields = (
  asset: AssetsSearchResp,
): Pick<BankPosition, 'isin' | 'name' | 'assetType'> & {
  ticker: string;
  infoAssetType: 'stock' | 'etf' | 'mutual_fund' | null;
} => ({
  isin: asset.isin ?? asset.ticker,
  name: asset.name,
  assetType: mapSearchAssetType(asset.asset_type),
  ticker: asset.ticker,
  infoAssetType: mapAssetsSearchToInfoAssetType(asset.asset_type),
});

export const mapAssetsInfoPriceToPositionFields = (info: {
  price?: number | null;
  price_as_of?: string | null;
}): Partial<Pick<BankPosition, 'price' | 'snapshotDate'>> => {
  const fields: Partial<Pick<BankPosition, 'price' | 'snapshotDate'>> = {};

  if (typeof info.price === 'number' && Number.isFinite(info.price)) {
    fields.price = info.price;
  }

  if (info.price_as_of) {
    const parsed = dayjs(info.price_as_of);

    if (parsed.isValid()) {
      fields.snapshotDate = parsed.format('YYYY-MM-DD');
    }
  }

  return fields;
};
